import { Card, Col, Row } from 'react-bootstrap'
import { useToasts } from 'react-toast-notifications'
import { useHistory } from 'react-router-dom'

import FormWizard, {
  FormWizardHandlers,
  Step,
  SubmitStatus
} from '../../../components/organisms/FormWizard'
import Step1, { ICreateSpeFormStep1 } from './Step1/step1'
import Step2, { ICreateSpeFormStep2 } from './Step2/step2'
import Step3, { ICreateSpeFormStep3 } from './Step3/step3'
import Step4, { ICreateSpeFormStep4 } from './Step4'
import { Title } from '../../../components/atoms/Title'
import React, { useState } from 'react'
import { SetableContext } from '../../../interfaces/setable-context'
import Step5 from './Step5'
import { SpeType } from '../../../domain/models/SpeModel'
import Utils from './utils'

import { ICreateSpe, IUpdateSpe } from '../../../domain/usecases/SPE'
import { getCompany } from '../../../domain/usecases/Company'

export type ICreateSpeForm = Partial<
  ICreateSpeFormStep1 &
    ICreateSpeFormStep2 &
    ICreateSpeFormStep3 &
    ICreateSpeFormStep4 & { id?: string }
>
export const CreateSpeContext = React.createContext<
  SetableContext<ICreateSpeForm> & { submitStatus?: SubmitStatus }
>({})

type Props = {
  handleCreate: ICreateSpe
  getCompaniesList: getCompany
  hasSpe?: SpeType
  handleUpdate: IUpdateSpe
  hasCompanyId: string
}

export default function CreateSpe({
  handleCreate,
  getCompaniesList,
  hasSpe,
  hasCompanyId,
  handleUpdate
}: Props) {
  const { addToast } = useToasts()
  const history = useHistory()
  const { convertSpeObjToExpectedFormatContext } = Utils()

  // Edição: os dados entram já no estado inicial (antes vinham num useEffect e
  // dependiam do passo 1 remontar para aparecer no formulário).
  const [data, setData] = useState<ICreateSpeForm>(() =>
    hasSpe
      ? convertSpeObjToExpectedFormatContext(hasSpe)
      : { proxies: [], companyId: hasCompanyId }
  )
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle')

  // Componente do passo 1 com identidade estável. Recriado a cada render, ele
  // remontava a cada toast (useToasts re-renderiza esta tela): apagava o que
  // foi digitado e, se a lista de incorporadoras falhasse, entrava em loop
  // (remonta → busca → falha → toast → remonta...).
  const Step1WithCompanies = React.useMemo(
    () =>
      function Step1WithCompanies(props: FormWizardHandlers) {
        return <Step1 getCompaniesList={getCompaniesList} props={props} />
      },
    []
  )

  const steps: Step[] = [
    {
      id: 1,
      label: 'Dados da SPE',
      component: Step1WithCompanies
    },
    {
      id: 2,
      label: 'NIRE',
      component: Step2
    },
    {
      id: 3,
      label: 'Procurador(es)',
      component: Step3
    },
    {
      id: 4,
      label: 'Meios de Pagamento',
      component: Step4
    },
    {
      id: 5,
      label: 'Final',
      component: Step5,
      disableContinueLater: true
    }
  ]
  /*eslint-disable */
  const updateSpe = () => {
    if (
      hasSpe?.commercial_board_document_url ===
      data?.commercial_board_document_url?.name
    ) {
      delete data.commercial_board_document_url
    }
    setSubmitStatus('pending')
    handleUpdate
      .updateOne(data)
      .then(() => setSubmitStatus('success'))
      .catch((error) => {
        setSubmitStatus('error')
        history.push('/spes')
        addToast('Não foi possível editar a SPE.', {
          appearance: 'error',
          autoDismiss: true
        })
      })
  }

  // Antes: sem await/catch, a tela final mostrava "cadastrados com sucesso!"
  // mesmo com o backend recusando (ex.: CNPJ já cadastrado). Agora o passo
  // final espera a resposta; em erro volta ao passo 4 com os dados e avisa.
  const createSpe = async () => {
    setSubmitStatus('pending')
    try {
      await handleCreate.createOne(data as Required<ICreateSpeForm>)
      setSubmitStatus('success')
    } catch (error) {
      setSubmitStatus('error')
      addToast(error?.message || 'Não foi possível cadastrar a SPE.', {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const onFinish = () => {
    hasSpe ? updateSpe() : createSpe()
  }

  const handleSave = () => {
    sessionStorage.setItem('CreateSpeForm', JSON.stringify(data))
  }

  return (
    <Row>
      <Col>
        <Card>
          <Card.Header>
            <Title>{hasSpe ? 'Editar' : 'Cadastrar'} SPE</Title>
          </Card.Header>
          <Card.Body>
            <CreateSpeContext.Provider value={{ data, setData, submitStatus }}>
              <FormWizard
                onSaveDraft={handleSave}
                steps={steps}
                finishOnStep={5}
                onFinish={onFinish}
                canClickOnSteps={!!hasSpe}
              />
            </CreateSpeContext.Provider>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}
